import 'project/polyfill'
import Link from 'next/link'
import classNames from 'classnames'
import { I18NConfig } from 'next/dist/server/config-shared'
import { useRouter } from 'next/router'

import {
  heroGap,
  sectionGap,
  marginHeader,
  sectionPadding,
  sectionGapSmall,
  sectionPaddingLarge,
} from 'project/padding'
import {
  TReviews,
  TFAQList,
  TPlanSummary,
  TBlogPostResult,
} from 'common/cms-types'
import SeoContainer, {
  SeoContainerProps,
} from 'components/util/containers/SeoContainer'
import { NextPageWithLayout } from '../types/nextPageWithLayout'
import { createClient } from '../prismicio'
import { Utils } from 'common/utils'
import { Query } from '@prismicio/types'
import { getPlans } from 'common/planData'
import Supademo from 'components/Supademo'
import Button from 'components/forms/Button'
import FAQList from 'components/util/FAQList'
import { HoxtonMixLogo } from 'components/icons'
import HeroBanner from 'components/hero/HeroBanner'
import HeadingAndText from 'components/HeadingAndText'
import standardiseTitle from 'project/standardiseTitle'
import MailPortalHighlights, {
  smallBusinessHighlights,
} from 'components/cards/new/common/MailPortalHighlights'
import APIDocsCard from 'components/cards/new/APIDocsCard'
import PartnersCard from 'components/cards/new/PartnersCard'
import ReviewsSection from 'components/sections/ReviewsSection'
import { getFAQList, getPosts, getReviews } from 'common/cms-sdk'
import ServiceIncludes from 'components/cards/new/ServiceIncludes'
import EmbededKonnectForm from 'components/sections/EmbededKonnectForm'
import Newsletter from 'components/accountancy-landing-pages/Newsletter'
import UltimateSolutionCard from 'components/cards/new/UltimateSolutionCard'
import { enterpriseBreadcrumb } from 'components/util/containers/Breadcrumbs'
import PartnersTestimonial from 'components/cards/new/PartnerTestimonialCard'
import WhoUsesOurSolutionCard from 'components/cards/new/WhoUsesOurSolutionCard'
import RegulatoryComplianceCard from 'components/cards/new/RegulatoryComplianceCard'
import EnterpriseBenefitsSection from 'components/sections/EnterpriseBenefitsSection'
import TargetMarketAndGrowthCard from 'components/cards/new/TargetMarketAndGrowthCard'

export type HomePageType = {
  posts: Query<TBlogPostResult>
  plans: TPlanSummary[]
  faqList: TFAQList
  reviews: TReviews
}

const HomePage: NextPageWithLayout<HomePageType> = ({ faqList, reviews }) => {
  const router = useRouter()
  const seoProps: SeoContainerProps['seoProps'] = {
    description:
      'Offer your clients a trusted London registered office address with compliant mail handling and scalable support. The Hoxton Mix is the go-to partner for banks, accountants, and incorporation firms.',
    openGraph: {
      url: 'https://www.hoxtonmix.com/enterprise/',
    },
    title: standardiseTitle(
      'Registered Office Services for Banks, Accountants & Formation Agents',
    ),
  }

  return (
    <SeoContainer
      alternate={[
        { href: '/au/enterprise', lang: 'en-au' },
        { href: '/enterprise', lang: 'en-gb' },
      ]}
      breadcrumb={enterpriseBreadcrumb()}
      seoProps={seoProps}
    >
      <main
        className={classNames(
          'overflow-x-hidden new-design d-flex flex-column bg-grey6',
          heroGap,
        )}
      >
        <HeroBanner
          logo={<HoxtonMixLogo className='mb-5 mb-lg-8' />}
          robotImage={{
            alt: 'HaiRo™ robot mascot holding envelopes',
            dimensions: {
              height: 1000,
              width: 777,
            },
            src: '/images/new/robot-holding-mail.png',
          }}
          cta={
            <a href='#konnect-form' className='btn btn-lg btn-primary mt-lg-8'>
              Book a Demo
            </a>
          }
          hideDownloadButtons
          title='Offer Your Clients a Registered Office Address Service'
          description={
            <div className={classNames('d-flex flex-column', sectionGapSmall)}>
              We provide you with a plug-and-play registered business and
              directors' service address solution that you can pass onto your
              clients to generate an incremental revenue stream and provide
              improved client services. Most importantly, it provides your
              clients with a service they actually want.
            </div>
          }
          subtitle={
            <span className='fs-fluid-24-48'>
              <span className='color-pink2 fw-semibold'>
                With mail handling and digital document management
              </span>
            </span>
          }
        />
        <div className={classNames('container d-flex flex-column', sectionGap)}>
          <div className={sectionPadding}>
            <HeadingAndText
              className={classNames('px-lg-0 px-6 ', marginHeader)}
              subtitle='Join our existing partners and enjoy benefits such as an incremental recurring revenue stream, lower acquisition costs, higher customer lifetime value, and increased customer satisfaction.'
              classesForTitle='fs-fluid-36-80 lh-105'
              subtitleSize='large'
              title={`An enterprise solution for registered office address services.`}
            />
            <ServiceIncludes />
          </div>
          <div className={`d-none d-lg-flex flex-column ${sectionGapSmall}`}>
            <div className={classNames('d-flex flex-column', sectionGapSmall)}>
              <div className={classNames(sectionPadding, 'd-flex flex-column')}>
                <h2
                  id='demo'
                  className={classNames(
                    'fs-fluid-36-80 lh-105 fw-semibold text-center',
                  )}
                >
                  Our <span className='color-pink2'>Partner Portal</span>.
                </h2>
                <h3
                  style={{
                    maxWidth: 682,
                  }}
                  className='fw-semibold text-muted mt-lg-3 mt-2 text-center fs-fluid-20-36 mx-auto'
                >
                  With the Partner Portal, you can manage your clients
                  seamlessly and efficiently.
                </h3>
              </div>
              <div>
                <Supademo showcaseId={'cmbs3di7n00vy090ismxakcn0'} />
              </div>
            </div>
          </div>

          <div className={classNames('d-flex flex-column gap-0')}>
            <div className={classNames(sectionPaddingLarge, marginHeader)}>
              <EmbededKonnectForm
                title={`Want to know more about how it works? <span class="color-pink2">Get in touch today</span>.`}
              />
            </div>
            <WhoUsesOurSolutionCard />
          </div>
          <EnterpriseBenefitsSection title='Discover the various sectors already benefiting from our registered office service.' />
          <PartnersCard book />
          <div>
            <h2
              className={classNames(
                'fs-fluid-36-80 lh-105 px-6 fw-semibold text-center',
                marginHeader,
                sectionPaddingLarge,
              )}
            >
              Thousands of companies have trusted us with over
              <br />
              <span className='color-pink2'>1 million pieces of mail</span>.
            </h2>
            <div className={marginHeader}>
              <PartnersTestimonial />
            </div>
            <div className={'text-center'}>
              <a
                href='#konnect-form'
                className='btn btn-lg px-22 w-100 w-lg-auto btn-primary'
              >
                Book a Demo
              </a>
            </div>
          </div>
          <TargetMarketAndGrowthCard />
          <div className={classNames('d-flex flex-column')}>
            <h2
              className={classNames(
                'fw-semibold text-center fs-fluid-36-80 lh-105',
                marginHeader,
                sectionPaddingLarge,
              )}
            >
              More than just a registered business address.
            </h2>
            <UltimateSolutionCard />
          </div>
          <div className={classNames('d-flex flex-column')}>
            <h2
              className={classNames(
                'fw-semibold text-center fs-fluid-36-80 lh-105',
                marginHeader,
                sectionPaddingLarge,
              )}
            >
              Ease of integration.
            </h2>
            <APIDocsCard />
          </div>
          <div className={classNames('d-flex flex-column gap-0')}>
            <h2
              className={classNames(
                'fw-semibold text-center fs-fluid-36-80 lh-105',
                marginHeader,
                sectionPaddingLarge,
              )}
            >
              Regulatory Compliance & Security Excellence.
            </h2>
            <RegulatoryComplianceCard />
          </div>
          <MailPortalHighlights
            title={
              'How our Registered office address service helps small businesses.'
            }
            subtitle={''}
            hideImage
          >
            {smallBusinessHighlights.map((highlight, index) => (
              <div className='pb-4' key={index}>
                <span className='fw-semibold'>{highlight.title}</span>
                <p
                  className='pt-4'
                  dangerouslySetInnerHTML={{ __html: highlight.content }}
                />
              </div>
            ))}
            <div className='text-center'>
              <div className='pt-4 fw-semibold'>
                Let us help you get started on your business journey.
              </div>
              <Link href={'/virtual-office-london-old-street'}>
                <Button
                  size='large'
                  className='w-100 w-lg-auto mt-2 px-22 mt-lg-13'
                >
                  View our registered office services
                </Button>
              </Link>
            </div>
          </MailPortalHighlights>
          <FAQList
            subtitle={
              'Learn more about how our Virtual Office service could benefit your business'
            }
            slices={faqList.data.slices}
          />

          <Newsletter />
        </div>
        <ReviewsSection data={reviews} pageUrl={router.pathname} includeJsonLd={false} />
      </main>
    </SeoContainer>
  )
}
const PAGE_SIZE = 4

export async function getStaticProps(
  context: I18NConfig & { locale?: string; params: { uid: string } },
) {
  try {
    const client = createClient(context)
    const lang = Utils.getLocaleID(context.locale || context.defaultLocale)
    const plans = await getPlans()
    const posts = await getPosts(client, 1, lang, PAGE_SIZE, undefined)
    const reviews = await getReviews()
    const faqList = await getFAQList(client, lang, 'enterprise')
    return {
      props: {
        faqList,
        plans,
        posts,
        reviews,
      },
    }
  } catch (e) {
    console.log(e)
    return {
      props: {},
    }
  }
}

export default HomePage
